import PropTypes from "prop-types"
import React, { Fragment, useEffect, useState } from "react"
import { useQuery } from "react-query"

import DataTable from "src/components/DataTable"
import Loader from "src/components/Loader"
import Tooltip from "src/components/Tooltip"

import { getNightDatatable } from "src/api/Manage/Reservations"

import useDebounce from "src/hooks/use_debounce"

import { updateUrlQueryParams } from "src/utils/url/parsing/marina"

import ReservationBadges from "./ReservationBadges"
import SpaceAssignmentCell from "./SpaceAssignmentCell"
import { formatDate, formatDimension } from "./helpers"

const ReservationNightTable = ({
  night,
  arrival,
  departure,
  bookingType,
  showingTonightsBoats,
  marinaName,
  marinaMcomId,
  paths,
  i18n,
  tonightsBoatsUpdatesFeatureFlagEnabled,
}) => {
  // Read initial state from URL parameters
  const urlParams = new URLSearchParams(window.location.search)
  const [page, setPage] = useState(parseInt(urlParams.get("page")) || 1)
  const [sortKey, setSortKey] = useState(urlParams.get("sort") || null)
  const [sortDirection, setSortDirection] = useState(
    urlParams.get("direction") || "asc"
  )

  // Debounced URL update function
  const [debouncedUpdateUrl] = useDebounce((params) => {
    updateUrlQueryParams(params)
  }, 300)

  // Update both state and URL parameters
  const updateParams = (newPage, newSortKey, newSortDirection) => {
    setPage(newPage)
    setSortKey(newSortKey)
    setSortDirection(newSortDirection)

    // Build URL parameters object, preserving existing filter params
    const urlParams = {
      ...(newPage > 1 && { page: newPage }),
      ...(newSortKey && { sort: newSortKey }),
      ...(newSortDirection && newSortKey && { direction: newSortDirection }),
      // Preserve existing filter parameters
      ...(arrival && { arrival: "true" }),
      ...(departure && { departure: "true" }),
      ...(bookingType && { booking_type: bookingType }),
    }

    debouncedUpdateUrl(urlParams)
  }

  // Track analytics like the legacy component
  useEffect(() => {
    if (showingTonightsBoats && window.Amplitude) {
      window.Amplitude.trackEvent("tonights_boats:screen_viewed", {
        marina_name: marinaName,
        marina_id: marinaMcomId,
      })
    }
  }, [showingTonightsBoats, marinaName, marinaMcomId])

  const handleColumnSort = (columnKey) => {
    let newSortKey = columnKey
    let newSortDirection = "asc"

    if (sortKey === columnKey) {
      if (sortDirection === "asc") {
        newSortDirection = "desc"
      } else if (sortDirection === "desc") {
        // Third click - clear sorting
        newSortKey = null
        newSortDirection = null
      }
    }

    // Always reset to first page when sorting changes
    updateParams(1, newSortKey, newSortDirection)
  }

  const hasNotes = (row) => {
    return !!(
      row.special_request_text ||
      row.reservation_note ||
      row.contact_note
    )
  }

  const renderCell = (content) => (row) => {
    return typeof content === "function" ? content(row) : content
  }

  const { data, isLoading, isFetching, isError, error } = useQuery(
    [
      "reservationNightDatatable",
      night,
      page,
      arrival,
      departure,
      bookingType,
      sortKey,
      sortDirection,
    ],
    () =>
      getNightDatatable(night, {
        page,
        ...(arrival && { arrival }),
        ...(departure && { departure }),
        ...(bookingType && { booking_type: bookingType }),
        ...(sortKey && { sort: sortKey }),
        ...(sortDirection && { direction: sortDirection }),
      }),
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      staleTime: 30000, // 30 seconds
      enabled: Boolean(night),
    }
  )

  const { data: printData, isLoading: isPrintLoading } = useQuery(
    [
      "reservationNightDatatablePrint",
      night,
      arrival,
      departure,
      bookingType,
      sortKey,
      sortDirection,
    ],
    () =>
      getNightDatatable(night, {
        all: true, // Tell backend to return all results without pagination
        ...(arrival && { arrival }),
        ...(departure && { departure }),
        ...(bookingType && { booking_type: bookingType }),
        ...(sortKey && { sort: sortKey }),
        ...(sortDirection && { direction: sortDirection }),
      }),
    {
      refetchOnWindowFocus: false,
      staleTime: 30000,
      enabled: Boolean(night),
    }
  )

  const reservations = data?.reservations || []
  const totalPages = data?.pagination?.total_pages || 1

  // Use original reservations data without augmentation for screen view
  const tableData = reservations

  // Use print data for print view (all reservations with filters applied)
  const printReservations = printData?.reservations || []

  // Disable/enable print button based on print data loading state
  useEffect(() => {
    const printButton =
      document.querySelector('a[href="javascript:window.print()"]') ||
      document.querySelector('a[href="#"]')
    if (printButton) {
      if (isPrintLoading) {
        printButton.classList.add("disabled")
        printButton.setAttribute("disabled", "disabled")
        printButton.href = "#"
        printButton.title = i18n.print.loadingTooltip
      } else {
        printButton.classList.remove("disabled")
        printButton.removeAttribute("disabled")
        printButton.href = "javascript:window.print()"
        printButton.title = i18n.print.readyTooltip
      }
    }
  }, [isPrintLoading])

  const renderBookingType = (row) => (
    <div className="flex flex-col">
      <div>{row.booking_type}</div>
      <div className="text-xs text-gray-600 print:text-xxs">
        #{row.booking_encoded_id}
      </div>
    </div>
  )

  const colDefs = [
    {
      key: "badges",
      header: i18n.headers.tags,
      width: "150px",
      render: (row) => <ReservationBadges reservation={row} />,
    },
    {
      key: "notes",
      header: i18n.headers.notes,
      width: "45px",
      render: (row) => {
        if (!hasNotes(row)) return <span></span>

        return (
          <Tooltip
            text={renderNoteContent(row)}
            placement="right"
            trigger="auto"
          >
            <div
              className="flex min-h-[40px] w-full cursor-help items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <i
                className="icon icon-clipboard-pencil text-lg text-blue-600"
                aria-label={i18n.tooltips.viewCustomerNotes}
              />
            </div>
          </Tooltip>
        )
      },
    },
    {
      key: "space",
      header: i18n.headers.assignment,
      width: "160px",
      sortable: true,
      render: (row) => (
        <SpaceAssignmentCell
          reservationId={row.encoded_id}
          currentNightSpaceName={row.current_night_space_name}
          hasMultipleSpaces={row.has_multiple_spaces}
          night={night}
        />
      ),
    },
    {
      key: "contact_name",
      header: i18n.headers.captain,
      width: "150px",
      cellAlign: "left",
      sortable: true,
      render: renderCell((row) => (
        <div className="truncate" title={row.contact_name}>
          {row.contact_name}
        </div>
      )),
    },
    {
      key: "boat_name",
      header: i18n.headers.boat,
      width: "120px",
      cellAlign: "left",
      sortable: true,
      render: renderCell((row) => (
        <div className="truncate" title={row.boat_name}>
          {row.boat_name}
        </div>
      )),
    },
    {
      key: "boat_type",
      header: i18n.headers.type,
      width: "50px",
      cellAlign: "left",
      sortable: true,
      render: renderCell((row) =>
        row.boat_type
          ? row.boat_type.charAt(0).toUpperCase() + row.boat_type.slice(1)
          : ""
      ),
    },
    {
      key: "loa",
      header: i18n.headers.loa,
      width: "75px",
      cellAlign: "left",
      sortable: true,
      render: renderCell((row) => formatDimension(row.loa)),
    },
    {
      key: "beam",
      header: i18n.headers.beam,
      width: "75px",
      cellAlign: "left",
      sortable: true,
      render: renderCell((row) => formatDimension(row.beam)),
    },
    {
      key: "draw",
      header: i18n.headers.draft,
      width: "75px",
      cellAlign: "left",
      sortable: true,
      render: renderCell((row) => formatDimension(row.draw)),
    },
    {
      key: "booking_type",
      header: i18n.headers.booking_type,
      width: "135px",
      cellAlign: "left",
      sortable: false,
      render: renderCell(renderBookingType),
    },
    {
      key: "check_in_date",
      header: i18n.headers.arrival,
      width: "110px",
      cellAlign: "left",
      sortable: true,
      render: renderCell((row) => formatDate(row.check_in_date)),
    },
    {
      key: "check_out_date",
      header: i18n.headers.departure,
      width: "110px",
      cellAlign: "left",
      sortable: true,
      render: renderCell((row) => formatDate(row.check_out_date)),
    },
    {
      key: "nights",
      header: i18n.headers.nights,
      width: "70px",
      cellAlign: "left",
      sortable: true,
      render: renderCell((row) => row.nights),
    },
    {
      key: "electric_display_name",
      header: i18n.headers.electric,
      width: "85px",
      cellAlign: "left",
      sortable: true,
      render: renderCell((row) => row.electric_display_name),
    },
  ]

  const rowConfig = {
    onClick: (row) => {
      // Navigate to reservation detail using server-provided path template
      const reservationPath = paths.reservationPathTemplate.replace(
        "{id}",
        row.encoded_id
      )
      window.location.href = reservationPath
    },
    variant: () => "default",
  }

  const renderNoteContent = (row) => {
    const notes = []

    if (row.special_request_text) {
      notes.push({
        label: i18n.noteLabels.specialRequest,
        content: row.special_request_text,
      })
    }

    if (row.reservation_note) {
      notes.push({
        label: i18n.noteLabels.reservationNote,
        content: row.reservation_note,
      })
    }

    if (row.contact_note) {
      notes.push({
        label: i18n.noteLabels.customerNote,
        content: row.contact_note,
      })
    }

    return (
      <div className="max-w-sm space-y-2 p-3 text-sm">
        {notes.map((note, index) => (
          <div key={index}>
            <div className="mb-1 font-semibold text-red-600">{note.label}:</div>
            <div className="whitespace-pre-wrap text-gray-800">
              {note.content}
            </div>
            {index < notes.length - 1 && (
              <hr className="my-2 border-gray-200" />
            )}
          </div>
        ))}
      </div>
    )
  }

  if (isError) {
    return (
      <div className="rounded border border-red-200 bg-red-50 p-4 text-center">
        <p className="text-red-800">
          {i18n.errors.loadingMessage.replace(
            "%{error}",
            error?.message || i18n.errors.loadingFallback
          )}
        </p>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <Loader />
      </div>
    )
  }
  const hasAssignments = (row) => {
    return row.assignments && row.assignments.length > 0
  }

  const renderAssignmentsRow = (row) => {
    if (!hasAssignments(row)) return null

    return (
      <tr
        key={`${row.id}-assignments`}
        className={`assignment-row hidden print:table-row ${
          hasNotes(row) ? "has-notes-after" : "last-detail-row"
        }`}
      >
        <td
          colSpan={colDefs.length}
          className="bg-gray-100 py-1 text-xs print:text-[7pt]"
        >
          <div className="leading-tight">
            {row.assignments.map((assignment, index) => (
              <span key={index}>
                <span className="font-bold">{assignment.space_name}</span>:{" "}
                {formatDate(assignment.start_date)}
                {assignment.start_date !== assignment.end_date &&
                  ` - ${formatDate(assignment.end_date)}`}
                {index < row.assignments.length - 1 ? " • " : ""}
              </span>
            ))}
          </div>
        </td>
      </tr>
    )
  }

  const renderNotesRow = (row) => {
    if (!hasNotes(row)) return null

    const notes = []
    if (row.special_request_text) {
      notes.push({
        label: i18n.noteLabels.specialRequest,
        content: row.special_request_text,
      })
    }
    if (row.reservation_note) {
      notes.push({
        label: i18n.noteLabels.reservationNote,
        content: row.reservation_note,
      })
    }
    if (row.contact_note) {
      notes.push({
        label: i18n.noteLabels.customerNote,
        content: row.contact_note,
      })
    }

    return (
      <tr
        key={`${row.id}-notes`}
        className="notes-row last-detail-row hidden print:table-row"
      >
        <td
          colSpan={colDefs.length}
          className="bg-gray-50 py-1 text-xs print:text-[7pt]"
        >
          <div className="leading-tight">
            {notes.map((note, index) => (
              <Fragment key={index}>
                <span className="font-bold">{note.label}:</span>
                <span className="ml-1">{note.content}</span>
                {index < notes.length - 1 && (
                  <span className="mx-2 font-bold">•</span>
                )}
              </Fragment>
            ))}
          </div>
        </td>
      </tr>
    )
  }

  return (
    <div className="space-y-4">
      {/* Regular DataTable for screen view */}
      <div className="print:hidden">
        {tonightsBoatsUpdatesFeatureFlagEnabled && (
          <div data-testid="reservation-night-table-filters">TODO: TB-2418</div>
        )}
        <DataTable
          name="reservationNightTable"
          colDefs={colDefs}
          rowData={tableData}
          rowConfig={rowConfig}
          pagination
          page={Number(page)}
          numberOfPages={Number(totalPages)}
          onPageChange={(pageData) =>
            updateParams(pageData.selected, sortKey, sortDirection)
          }
          sortConfig={{
            sortKey,
            sortDirection,
            onColumnSort: handleColumnSort,
          }}
          isLoading={isFetching}
          hideActions
        />
      </div>

      {/* Custom table for print view with notes below rows - only render when there's data */}
      {printReservations.length > 0 && (
        <div className="hidden print:block">
          <table className="reservation-night-print-table table-default table-hover table w-full">
            <thead>
              <tr className="reservation-headers">
                <th>{i18n.headers.tags}</th>
                <th>{i18n.headers.captain}</th>
                <th>{i18n.headers.boat}</th>
                <th>{i18n.headers.type}</th>
                <th>{i18n.headers.loa}</th>
                <th>{i18n.headers.beam}</th>
                <th>{i18n.headers.draft}</th>
                <th>{i18n.headers.booking_type}</th>
                <th>{i18n.headers.arrival}</th>
                <th>{i18n.headers.departure}</th>
                <th>{i18n.headers.nights}</th>
                <th>{i18n.headers.electric}</th>
              </tr>
            </thead>
            <tbody>
              {printReservations.map((row) => (
                <Fragment key={`print-${row.id}`}>
                  <tr
                    className={
                      hasAssignments(row) || hasNotes(row)
                        ? "reservation-with-details"
                        : ""
                    }
                  >
                    <td>
                      <ReservationBadges reservation={row} />
                    </td>
                    <td>{row.contact_name}</td>
                    <td>{row.boat_name}</td>
                    <td>
                      {row.boat_type
                        ? row.boat_type.charAt(0).toUpperCase() +
                          row.boat_type.slice(1)
                        : ""}
                    </td>
                    <td>{formatDimension(row.loa)}</td>
                    <td>{formatDimension(row.beam)}</td>
                    <td>{formatDimension(row.draw)}</td>
                    <td>{renderBookingType(row)}</td>
                    <td>{formatDate(row.check_in_date)}</td>
                    <td>{formatDate(row.check_out_date)}</td>
                    <td>{row.nights}</td>
                    <td>{row.electric_display_name}</td>
                  </tr>
                  {renderAssignmentsRow(row)}
                  {renderNotesRow(row)}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

ReservationNightTable.propTypes = {
  night: PropTypes.string.isRequired,
  arrival: PropTypes.bool,
  departure: PropTypes.bool,
  bookingType: PropTypes.oneOf(["reservation", "contract"]),
  showingTonightsBoats: PropTypes.bool,
  marinaName: PropTypes.string,
  marinaMcomId: PropTypes.string,
  paths: PropTypes.shape({
    reservationPathTemplate: PropTypes.string.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    headers: PropTypes.object.isRequired,
    tooltips: PropTypes.object.isRequired,
    noteLabels: PropTypes.object.isRequired,
    print: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
  }).isRequired,
  tonightsBoatsUpdatesFeatureFlagEnabled: PropTypes.bool.isRequired,
}

export default ReservationNightTable
